import { useMemo, useState } from "react"
import { useAppSelector } from "@/reducers/hooks"
import { RouteStop } from "@/reducers/routes2gSlice"
import { selectAllWorkflows } from "@/reducers/openclawWorkflowsSlice"
import { rulesForStop } from "@/lib/stopAutomations"
import { useAgentForDriverPerChat } from "@/hooks/useAgentForDriverPerChat"

/**
 * Everything the stop row needs to know about rules pinned to it. Kept out of
 * `RouteStopView` because the three places that use it — a menu item, a badge
 * and the dialog — sit in three different cells and would otherwise each carry
 * their own slice of this state.
 */
export const useStopAutomation = (stop: RouteStop) => {
  // Pinning an automation to a stop arrived with the driver workspace, so it
  // hides with it.
  const enabled = useAgentForDriverPerChat()
  const [isOpen, setOpen] = useState(false)
  const workflows = useAppSelector(selectAllWorkflows)

  // A stop still being added in the editor has no id, so nothing can be pinned
  // to it — the rules are always empty until the route is sent.
  const { pinned } = useMemo(
    () => rulesForStop(workflows, stop.id ?? ""),
    [workflows, stop.id],
  )

  const label = useMemo(
    () =>
      [stop.location?.companyName, stop.location?.formattedAddress]
        .filter(Boolean)
        .join(" · ") || "this stop",
    [stop.location?.companyName, stop.location?.formattedAddress],
  )

  return {
    /** Whether the stop can carry automations at all. */
    available: enabled && Boolean(stop.id),
    pinnedCount: enabled && stop.id ? pinned.length : 0,
    label,
    isOpen,
    setOpen,
  }
}
